import React, { useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Checkbox,
    FormControl,
    IconButton,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    useTestCategories,
    useCreateTestCategory,
    useUpdateTestCategory,
    useDeleteTestCategory,
} from '../../hooks/testCategories';
import type { TestCategory, TestCategoryStatus, CreateTestCategoryDto } from '../../types/types';
import {
    kCardColors,
    kDefaultItemsName,
    kItemsNameExamples,
    kTestCategoryStatuses,
} from '../../testCategoryConstants';
import mediaFilesService from '../../api/mediaFilesService';
import MediaFilePicker from './MediaFilePicker';
import { useEntitlements } from '../../hooks/entitlements';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import { t } from '../../i18n/t';

function resolveIconUrl(mediaFileId?: number | null, iconSvg?: string | null): string | null {
    return mediaFilesService.resolveFileUrl(mediaFileId, iconSvg);
}

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

const statusLabel = (s: string) => {
    const key = `testCategories.status.${s}` as const;
    const label = t(key);
    return label === key ? s : label;
};

const DashboardTestCategories = () => {
    const { data: categories = [], isLoading, refetch } = useTestCategories();
    const deleteMutation = useDeleteTestCategory();
    const [createOpen, setCreateOpen] = useState(false);
    const [editing, setEditing] = useState<TestCategory | null>(null);

    const handleDelete = async (cat: TestCategory) => {
        if (!window.confirm(t('testCategories.deleteConfirm', { type: cat.type }))) return;
        try {
            await deleteMutation.mutateAsync(cat.id);
        } catch (e) {
            console.error(e);
            alert(t('testCategories.deleteFailed'));
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
                {t('testCategories.title')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button variant="contained" onClick={() => setCreateOpen(true)}>
                    {t('testCategories.new')}
                </Button>
                <Tooltip title={t('testCategories.refresh')}>
                    <IconButton onClick={() => refetch()}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('testCategories.colIcon')}</TableCell>
                            <TableCell>{t('testCategories.colType')}</TableCell>
                            <TableCell>{t('testCategories.colName')}</TableCell>
                            <TableCell>{t('testCategories.colItemsName')}</TableCell>
                            <TableCell>{t('testCategories.colSortOrder')}</TableCell>
                            <TableCell>{t('testCategories.colStatus')}</TableCell>
                            <TableCell align="right">{t('testCategories.colActions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {categories.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    {t('testCategories.empty')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            categories.map((cat) => {
                                const iconSrc = resolveIconUrl(cat.mediaFileId, cat.iconSvg);
                                const accentColor = cat.iconColorHex || '#4B9FFF';
                                return (
                                <TableRow key={cat.id}>
                                    <TableCell>
                                        <Box
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: '50%',
                                                bgcolor: `${accentColor}20`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {iconSrc ? (
                                                <Box
                                                    component="img"
                                                    src={iconSrc}
                                                    alt=""
                                                    sx={{ width: 20, height: 20, objectFit: 'contain' }}
                                                />
                                            ) : (
                                                <HealthAndSafetyIcon sx={{ fontSize: 20, color: accentColor }} />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{cat.type}</TableCell>
                                    <TableCell>{cat.name}</TableCell>
                                    <TableCell>{cat.itemsName}</TableCell>
                                    <TableCell>{cat.sortOrder}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={statusLabel(cat.status)}
                                            color={cat.status === 'active' ? 'success' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title={t('common.edit')}>
                                            <IconButton size="small" onClick={() => setEditing(cat)}>
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('common.delete')}>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDelete(cat)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {createOpen && (
                <TestCategoryFormDialog
                    title={t('testCategories.dialogNew')}
                    onClose={() => setCreateOpen(false)}
                />
            )}
            {editing && (
                <TestCategoryFormDialog
                    title={t('testCategories.dialogEdit')}
                    category={editing}
                    onClose={() => setEditing(null)}
                />
            )}
        </Box>
    );
};

interface TestCategoryFormDialogProps {
    title: string;
    category?: TestCategory;
    onClose: () => void;
}

const TestCategoryFormDialog: React.FC<TestCategoryFormDialogProps> = ({ title, category, onClose }) => {
    const createMutation = useCreateTestCategory();
    const updateMutation = useUpdateTestCategory();
    const { data: catalogEntitlements = [] } = useEntitlements();
    const isEdit = category != null;

    const [type, setType] = useState(category?.type ?? '');
    const [name, setName] = useState(category?.name ?? '');
    const [description, setDescription] = useState(category?.description ?? '');
    const [itemsName, setItemsName] = useState(category?.itemsName ?? kDefaultItemsName);
    const [mediaFileId, setMediaFileId] = useState<number | null>(category?.mediaFileId ?? null);
    const [iconColorHex, setIconColorHex] = useState(category?.iconColorHex ?? kCardColors[0].hex);
    const [sortOrder, setSortOrder] = useState(category?.sortOrder ?? 0);
    const [status, setStatus] = useState<TestCategoryStatus>(category?.status ?? 'active');
    const [entitlementIds, setEntitlementIds] = useState<number[]>(category?.entitlementIds ?? []);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!type.trim()) {
            alert(t('testCategories.enterType'));
            return;
        }
        if (!name.trim()) {
            alert(t('testCategories.enterName'));
            return;
        }
        if (!description.trim()) {
            alert(t('testCategories.enterDescription'));
            return;
        }
        if (!HEX_RE.test(iconColorHex)) {
            alert(t('testCategories.invalidHex'));
            return;
        }
        if (mediaFileId == null) {
            alert(t('postureInstructions.selectImage'));
            return;
        }

        const dto: CreateTestCategoryDto = {
            type: type.trim(),
            name: name.trim(),
            description: description.trim(),
            itemsName: itemsName.trim() || kDefaultItemsName,
            mediaFileId,
            iconColorHex,
            sortOrder: Number(sortOrder),
            status,
            entitlementIds,
        };

        setSaving(true);
        try {
            if (isEdit) {
                await updateMutation.mutateAsync({ id: category.id, dto });
            } else {
                await createMutation.mutateAsync(dto);
            }
            onClose();
        } catch (e) {
            console.error(e);
            alert(t('testCategories.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label={t('testCategories.fieldType')}
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        size="small"
                        fullWidth
                    />
                    <TextField
                        label={t('testCategories.fieldName')}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        size="small"
                        fullWidth
                    />
                    <TextField
                        label={t('testCategories.fieldDescription')}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        multiline
                        minRows={2}
                        size="small"
                        fullWidth
                    />
                    <TextField
                        label={t('testCategories.fieldItemsName')}
                        value={itemsName}
                        onChange={(e) => setItemsName(e.target.value)}
                        size="small"
                        fullWidth
                        helperText={kItemsNameExamples.join(', ')}
                    />
                    <MediaFilePicker
                        labelKey="testCategories.fieldMediaFile"
                        kind="image"
                        required
                        value={mediaFileId}
                        onChange={setMediaFileId}
                    />
                    <FormControl size="small" fullWidth>
                        <InputLabel>{t('testCategories.fieldIconColor')}</InputLabel>
                        <Select
                            label={t('testCategories.fieldIconColor')}
                            value={iconColorHex}
                            onChange={(e) => setIconColorHex(e.target.value)}
                        >
                            {kCardColors.map((c) => (
                                <MenuItem key={c.hex} value={c.hex}>
                                    {c.name} ({c.hex})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label={t('testCategories.fieldSortOrder')}
                        type="number"
                        value={sortOrder}
                        onChange={(e) => setSortOrder(Number(e.target.value))}
                        size="small"
                        fullWidth
                    />
                    <FormControl size="small" fullWidth>
                        <InputLabel>{t('testCategories.fieldStatus')}</InputLabel>
                        <Select
                            label={t('testCategories.fieldStatus')}
                            value={status}
                            onChange={(e) => setStatus(e.target.value as TestCategoryStatus)}
                        >
                            {kTestCategoryStatuses.map((s) => (
                                <MenuItem key={s} value={s}>
                                    {statusLabel(s)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" fullWidth>
                        <InputLabel>{t('testCategories.entitlementIds')}</InputLabel>
                        <Select
                            multiple
                            label={t('testCategories.entitlementIds')}
                            value={entitlementIds}
                            onChange={(e) =>
                                setEntitlementIds(
                                    typeof e.target.value === 'string'
                                        ? []
                                        : (e.target.value as number[]),
                                )
                            }
                            input={<OutlinedInput label={t('testCategories.entitlementIds')} />}
                            renderValue={(selected) =>
                                selected
                                    .map(
                                        (id) =>
                                            catalogEntitlements.find((ent) => ent.id === id)?.type ??
                                            id,
                                    )
                                    .join(', ')
                            }
                        >
                            {catalogEntitlements.map((entitlement) => (
                                <MenuItem key={entitlement.id} value={entitlement.id}>
                                    <Checkbox checked={entitlementIds.includes(entitlement.id)} />
                                    <ListItemText
                                        primary={entitlement.type}
                                        secondary={entitlement.description}
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DashboardTestCategories;
